import type { CSSProperties, ReactNode } from 'react';

function FieldShell({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <span className="text-[11px] font-medium uppercase tracking-wider text-slate-500">{label}</span>
        {hint && <span className="font-mono text-[11px] font-semibold text-slate-400">{hint}</span>}
      </div>
      {children}
    </div>
  );
}

type NumberFieldProps = {
  label: string;
  value: number;
  onChange: (v: number) => void;
  suffix?: string;
  min?: number;
  step?: number;
  hint?: string;
  accent?: string;
};

export function NumberField({
  label,
  value,
  onChange,
  suffix,
  min,
  step = 1,
  hint,
  accent = '#7C3AED',
}: NumberFieldProps) {
  return (
    <FieldShell label={label} hint={hint}>
      <div className="relative">
        <input
          type="number"
          value={value}
          min={min}
          step={step}
          onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
          className="tinput w-full rounded-xl px-3.5 py-2.5 pr-14 font-mono text-sm font-semibold text-slate-800"
          style={{ '--field-accent': accent } as CSSProperties}
        />
        {suffix && (
          <span className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 font-mono text-xs text-slate-400">
            {suffix}
          </span>
        )}
      </div>
    </FieldShell>
  );
}

type SelectFieldProps = {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  hint?: string;
  accent?: string;
};

export function SelectField({ label, value, onChange, options, hint, accent = '#7C3AED' }: SelectFieldProps) {
  return (
    <FieldShell label={label} hint={hint}>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="tinput w-full cursor-pointer appearance-none rounded-xl px-3.5 py-2.5 pr-10 font-mono text-sm font-semibold text-slate-800"
          style={{ '--field-accent': accent } as CSSProperties}
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <i className="ri-arrow-down-s-line pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
      </div>
    </FieldShell>
  );
}

type SliderFieldProps = {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  step?: number;
  display: string;
  accent?: string;
};

export function SliderField({ label, value, onChange, min, max, step = 0.1, display, accent = '#7C3AED' }: SliderFieldProps) {
  const percent = ((value - min) / (max - min)) * 100;
  return (
    <FieldShell label={label} hint={display}>
      <div className="px-0.5 pt-1">
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(parseFloat(e.target.value))}
          style={
            {
              '--fill': `${percent}%`,
              '--slider-accent': accent,
              '--slider-track': '#e2e8f0',
            } as CSSProperties
          }
        />
      </div>
    </FieldShell>
  );
}

type SegmentedControlProps = {
  options: { value: string; label: string }[];
  value: string;
  onChange: (v: string) => void;
  accent: string;
};

export function SegmentedControl({ options, value, onChange, accent }: SegmentedControlProps) {
  return (
    <div className="flex rounded-xl bg-slate-100 p-1">
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            className="flex-1 whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-semibold transition-all"
            style={
              active
                ? { backgroundColor: accent, color: '#ffffff', boxShadow: `0 2px 10px ${accent}44` }
                : { color: '#64748b' }
            }
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

type QuickChipsProps = {
  options: number[];
  value: number;
  onChange: (v: number) => void;
};

export function QuickChips({ options, value, onChange }: QuickChipsProps) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((o) => (
        <button
          key={o}
          type="button"
          onClick={() => onChange(o)}
          className={`whitespace-nowrap rounded-lg px-2.5 py-1 font-mono text-[11px] font-semibold transition-colors ${
            value === o
              ? 'bg-violet-600 text-white'
              : 'bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-700'
          }`}
        >
          {o}
        </button>
      ))}
    </div>
  );
}

export function ResultPanel({ accent, children }: { accent: string; children: ReactNode }) {
  return (
    <div
      className="relative overflow-hidden rounded-2xl border p-5"
      style={{
        borderColor: `${accent}33`,
        background: `linear-gradient(135deg, ${accent}12 0%, #ffffff 60%)`,
      }}
    >
      <div
        className="pointer-events-none absolute -right-12 -top-12 h-36 w-36 rounded-full blur-3xl"
        style={{ background: `${accent}26` }}
      />
      <div className="relative space-y-3">{children}</div>
    </div>
  );
}

export function ResultItem({
  label,
  value,
  accent,
  big,
}: {
  label: string;
  value: string;
  accent: string;
  big?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-xs text-slate-500">{label}</span>
      <span
        className={`whitespace-nowrap font-mono font-bold tabular-nums ${big ? 'text-2xl' : 'text-sm'}`}
        style={{ color: accent, textShadow: big ? `0 0 28px ${accent}44` : undefined }}
      >
        {value}
      </span>
    </div>
  );
}

export function ResultNote({ children }: { children: ReactNode }) {
  return <p className="text-[11px] leading-relaxed text-slate-400">{children}</p>;
}